import type { CpUserInfoDetails } from '@bcf-vanilla-ts-v1-api-access/cp-api/models/cp-user-info-details';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import type { AnalyticsAction } from '@bcf-vanilla-ts-v1-shared/misc/analytics-shared-types/types';
import type { PushPageView } from '@bcf-vanilla-ui-ts-v1/shared/analytics/check-page-analytics';
import { isDisableCsrGtm } from '@bcf2-v1-shared/ssr/disable-csr-gtm';
import type { Observable } from 'rxjs';
import { ReplaySubject, combineLatest, first, fromEvent, map, mergeMap, startWith } from 'rxjs';
import type { ConsentAction } from './types';
import { analyticsTypesMap } from './types-mapper';

interface WindowLike extends Window {
  gtag?: (event: string, action: ConsentAction, data: Record<string, any>) => void;
  dataLayer?: {
    push: (payload: Record<string, any>) => void;
  };
  isGtmLazyLoaded?: boolean;
}

type DataLayerQueuePayload = {
  action: AnalyticsAction;
  payload?: Record<string, any>;
};

type GtagQueuePayload = {
  event: string;
  action: ConsentAction;
  data: Record<string, any>;
};

type AnalyticsQueue =
  | { type: 'gtag'; payload: GtagQueuePayload }
  | { type: 'dataLayer'; payload: DataLayerQueuePayload };

type PageViewQueuePayload = {
  url: string;
  payload?: Record<string, any>;
};
export class GtmService implements PushPageView {
  private _analyticsQueue$: ReplaySubject<AnalyticsQueue> = new ReplaySubject<AnalyticsQueue>();
  private _pageViewQueue$: ReplaySubject<PageViewQueuePayload> = new ReplaySubject<PageViewQueuePayload>();
  public userInfoDetails$: ReplaySubject<CpUserInfoDetails | undefined> = new ReplaySubject<
    CpUserInfoDetails | undefined
  >();
  public userRegistrationDateFromTodayElapsedDays$: ReplaySubject<number | undefined> = new ReplaySubject<
    number | undefined
  >(1);

  constructor(private _window?: WindowLike) {
    if (__RENDER_MODE__ === 'browser') {
      this._init();
    }
  }

  private _init(): void {
    if (typeof this._window === 'undefined') {
      return;
    }
    if (isDisableCsrGtm()) {
      return;
    }
    const gtmLoaded$: Observable<boolean> = fromEvent(this._window, 'gtmLoaded').pipe(
      map(() => true),
      startWith(this._window!.isGtmLazyLoaded),
      first((event: boolean | undefined): event is boolean => Boolean(event))
    );

    gtmLoaded$
      .pipe(
        first(() => Boolean(this._window!.dataLayer && this._window!.gtag)),
        mergeMap(() => this._analyticsQueue$)
      )
      .subscribe((analyticsQueue: AnalyticsQueue) => {
        if (analyticsQueue.type === 'gtag') {
          const gtagQueue: GtagQueuePayload = analyticsQueue.payload;
          this._window!.gtag!(gtagQueue.event, gtagQueue.action, gtagQueue.data);
        } else {
          const dataLayerQueue: DataLayerQueuePayload = analyticsQueue.payload;
          this._window!.dataLayer!.push({
            event: analyticsTypesMap(dataLayerQueue.action),
            ...(dataLayerQueue.payload ?? {})
          });
        }
      });

    gtmLoaded$
      .pipe(
        first(() => Boolean(this._window!.dataLayer)),
        mergeMap(() =>
          combineLatest([this._pageViewQueue$, this.userInfoDetails$, this.userRegistrationDateFromTodayElapsedDays$])
        )
      )
      .subscribe(
        ([pageViewQueue, userInfoDetails, registrationDaysElapsed]: [
          PageViewQueuePayload,
          CpUserInfoDetails | undefined,
          number | undefined
        ]) => {
          const dataLayerPayload: Record<string, any> = {
            page_view: pageViewQueue.url,
            ...(pageViewQueue.payload ?? {})
          };

          if (userInfoDetails) {
            dataLayerPayload.email = userInfoDetails.email;
            dataLayerPayload.phone = userInfoDetails.telephone;
            dataLayerPayload.wallet_id = userInfoDetails.walletId;
            if (registrationDaysElapsed && registrationDaysElapsed < 30) {
              dataLayerPayload.days_since_registration = registrationDaysElapsed;
            }
          }

          this._window?.dataLayer!.push(dataLayerPayload);
        }
      );
  }

  public gtag(event: string, action: ConsentAction, data: Record<string, any>): void {
    this._analyticsQueue$.next({ type: 'gtag', payload: { event, action, data } });
  }

  public pushDataLayer(action: AnalyticsAction, payload?: Record<string, any>): void {
    this._analyticsQueue$.next({ type: 'dataLayer', payload: { action, payload } });
  }

  public pushPageView(url: string, payload?: Record<string, any>): void {
    this._pageViewQueue$.next({ url, payload });
  }
}

export function provideGtmService(): GtmService {
  if (__RENDER_MODE__ === 'server') {
    return provideSingleton(GtmService, () => new GtmService(undefined));
  }
  return provideSingleton(GtmService, () => new GtmService(window));
}
